import { Spacer, TextCapitalized, SelectDS, Avatar, Tag } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "../../../../redux/hooks";
import styled from "styled-components";
import {
  selectManagersInCompanyGroup,
  selectManagersList,
} from "../usersSlice";
import "../../../../i18n/i18n";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { Option } from "@qivia/ui/src/designSystem/components/Select";

export type SelectExistingUserProps = {
  selectedExistingManagerUuid: (existingManagerUuid: string | null) => void;
};

export const SelectExistingUser = (props: SelectExistingUserProps) => {
  const { t } = useTranslation();
  const { selectedExistingManagerUuid } = props;
  const managersInCompanyGroup = useAppSelector(selectManagersInCompanyGroup);
  const managers = useAppSelector(selectManagersList);

  const [existingManagerUuidOption, setExistingManagerUuidOption] =
    useState<Option<string> | null>(null);

  const onlyManagersFromOtherCompanies = useMemo(() => {
    if (!managersInCompanyGroup) return [];
    return managersInCompanyGroup.filter(
      (manager) =>
        manager.role !== "DEPARTMENT_USER" &&
        manager.role !== "OWNER" &&
        !managers.some((m) => m.uuid === manager.uuid),
    );
  }, [managersInCompanyGroup, managers]);

  const managersOptions = useMemo(() => {
    if (!onlyManagersFromOtherCompanies) return [];

    return onlyManagersFromOtherCompanies.map((manager) => ({
      value: manager.uuid,
      label: (() => {
        const firstName = manager.firstName.split(" ")[0];
        const lastName = manager.lastName.split(" ")[0];
        const roleLabel = t(`users.management.role.${manager.role}`);
        return (
          <StyledFlex>
            <Avatar
              size={"S"}
              color={"green"}
              firstName={firstName}
              lastName={lastName}
            />
            <Spacer x={1} />
            <StyledColumn>
              <TextCapitalized>
                {manager.firstName} {manager.lastName}
              </TextCapitalized>
              <StyledUserEmail>{manager.email}</StyledUserEmail>
            </StyledColumn>
            <Tag
              text={roleLabel}
              textColor={colors["colors/text/black"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              borderColor={colors["colors/borders/cells/cells"]}
            />
          </StyledFlex>
        );
      })(),
    }));
  }, [onlyManagersFromOtherCompanies, t]);

  const filterOptions = useCallback(
    (option: Option<string> | null, inputValue: string) => {
      const manager = onlyManagersFromOtherCompanies.find(
        (m) => m.uuid === option?.value,
      );
      if (!manager) return false;

      const input = inputValue.toLowerCase().trim();
      return (
        manager.firstName.toLowerCase().includes(input) ||
        manager.lastName.toLowerCase().includes(input) ||
        manager.email.toLowerCase().includes(input)
      );
    },
    [onlyManagersFromOtherCompanies],
  );

  return (
    <>
      <StyledLeftAlign>
        <TextCapitalized>{t(`users.modal.informations.search`)}</TextCapitalized>
      </StyledLeftAlign>
      <Spacer y={1} />
      <SelectDS
        customHeight={3.5}
        allWidth
        isClearable
        label=""
        placeholder={t(`users.modal.informations.search.placeholder`)}
        options={managersOptions}
        filterOption={filterOptions}
        emptyErrorMessage={t(
          `users.modal.informations.search.emptyErrorMessage`,
        )}
        onChange={(selectedOption) => {
          setExistingManagerUuidOption(selectedOption ?? null);
          selectedExistingManagerUuid(selectedOption?.value ?? null);
        }}
        value={existingManagerUuidOption}
      />
      <Spacer y={1} />
      <StyledDividerContainer>
        <Spacer x={1} />
        {t(`users.modal.informations.or`)}
        <Spacer x={1} />
      </StyledDividerContainer>
    </>
  );
};

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledUserEmail = styled.div`
  color: ${colors["colors/text/lightGrey"]};
  font-weight: 300;
`;

const StyledDividerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  color: ${colors["colors/text/lightGrey"]};

  &::before,
  &::after {
    flex: 1;
    content: "";
    height: 0.0625rem;
    background-color: ${colors["colors/borders/input/default"]};
  }
`;

const StyledLeftAlign = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`;